import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  LinearProgress,
  Grid,
  Avatar,
  Paper,
} from "@mui/material";
import {
  LocalFireDepartment as FireIcon,
  EmojiEvents as TrophyIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store/Index";
import type { EndpointRequest, DeveloperStats } from "../types";
import apiService from "../../../service/Api";

interface DeveloperStatsProps {
  requests: EndpointRequest[];
  currentUser: string;
}

export const DeveloperStatsComponent: React.FC<DeveloperStatsProps> = ({
  requests,
  currentUser,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const primaryColor = reduxTheme.primary_colors?.[400] || "#724E41";

  const [stats, setStats] = useState<DeveloperStats>({
    username: currentUser,
    totalRequests: 0,
    currentStreak: 0,
    longestStreak: 0,
  });
  const [loadingStats, setLoadingStats] = useState(false);

  const loadTotalRequests = useCallback(async () => {
    setLoadingStats(true);
    try {
      const response = await apiService.get<{
        total_requests: number;
        status_counts: {
          completed?: number;
          pending?: number;
          in_progress?: number;
          blocked?: number;
          cancelled?: number;
        };
      }>("/api/endpoint-request/endpoint-requests/statistics/");

      setStats((prev) => ({
        ...prev,
        totalRequests: response.data.total_requests || 0,
      }));
    } catch (error: unknown) {
      console.error("Error loading total requests:", error);
      const userRequests = requests.filter(
        (r) => r.createdBy?.toLowerCase() === currentUser.toLowerCase()
      );
      setStats((prev) => ({
        ...prev,
        totalRequests: userRequests.length,
      }));
    } finally {
      setLoadingStats(false);
    }
  }, [currentUser, requests]);

  const loadDeveloperAwards = useCallback(async () => {
    try {
      const response = await apiService.get<{
        date_awarded: string;
        leaderboard: Array<{
          user_id: number;
          name: string;
          requests_count: number;
          awarded_points: number;
        }>;
      }>("/api/endpoint-request/endpoint-requests/daily-leaderboard/");

      const developerEntry = response.data.leaderboard.find(
        (dev) => dev.name.toLowerCase() === currentUser.toLowerCase()
      );

      setStats((prev) => ({
        ...prev,
        points: developerEntry?.awarded_points ?? 0,
      }));
    } catch (error: unknown) {
      console.error("Error loading developer awards:", error);
    }
  }, [currentUser]);

  const calculateStreaks = useCallback(() => {
    const userRequests = requests.filter(
      (r) => r.createdBy?.toLowerCase() === currentUser.toLowerCase()
    );

    if (userRequests.length === 0) {
      setStats((prev) => ({
        ...prev,
        currentStreak: 0,
      }));
      return;
    }

    const sortedRequests = [...userRequests].sort(
      (a, b) => new Date(a.requestDate).getTime() - new Date(b.requestDate).getTime()
    );

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const mostRecentRequest = sortedRequests[sortedRequests.length - 1];
    const mostRecentDate = new Date(mostRecentRequest.requestDate);
    mostRecentDate.setHours(0, 0, 0, 0);

    const daysSinceLastRequest = Math.floor(
      (today.getTime() - mostRecentDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    let currentStreak = 0;
    if (daysSinceLastRequest === 0 || daysSinceLastRequest === 1) {
      let streakCount = 1;
      let checkDate = new Date(mostRecentDate);

      for (let i = sortedRequests.length - 2; i >= 0; i--) {
        const prevDate = new Date(sortedRequests[i].requestDate);
        prevDate.setHours(0, 0, 0, 0);
        const diff = Math.floor((checkDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24));

        if (diff === 1) {
          streakCount++;
          checkDate = prevDate;
        } else if (diff > 1) {
          break;
        }
      }

      currentStreak = streakCount;
    }

    setStats((prev) => ({
      ...prev,
      currentStreak,
      lastRequestDate: mostRecentRequest.requestDate,
    }));
  }, [requests, currentUser]);

  useEffect(() => {
    loadTotalRequests();
    loadDeveloperAwards();
  }, [loadTotalRequests, loadDeveloperAwards]);

  useEffect(() => {
    calculateStreaks();
  }, [calculateStreaks]);

  useEffect(() => {
    const handlePointsUpdate = (event: CustomEvent) => {
      if (event.detail?.username === currentUser) {
        calculateStreaks();
        loadDeveloperAwards();
      }
    };

    window.addEventListener(
      "developer-points-updated",
      handlePointsUpdate as EventListener
    );

    return () => {
      window.removeEventListener(
        "developer-points-updated",
        handlePointsUpdate as EventListener
      );
    };
  }, [calculateStreaks, currentUser, loadDeveloperAwards]);

  const getStreakMessage = (streak: number) => {
    if (streak === 0) return "Start your streak today!";
    if (streak === 1) return "Great start! Keep it going!";
    if (streak < 7) return `${streak} day streak!`;
    if (streak < 14) return `${streak} day streak! Amazing!`;
    return `${streak} day streak! Legendary!`;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3.5,
        p: 3,
        borderRadius: "20px",
        background: `linear-gradient(135deg, ${primaryColor}E6 0%, ${primaryColor}B3 100%)`,
        color: "white",
        boxShadow: "0 10px 30px -5px rgba(0, 0, 0, 0.15)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Avatar
          sx={{
            bgcolor: "rgba(255, 255, 255, 0.2)",
            width: 52,
            height: 52,
            backdropFilter: "blur(4px)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          <TrophyIcon sx={{ fontSize: 28, color: "#F59E0B" }} />
        </Avatar>
        <Box>
          <Typography variant="h5" fontWeight="800" sx={{ letterSpacing: "-0.5px" }}>
            Developer Stats & Leaderboard
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Logged in as <strong>{currentUser}</strong>
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card
            sx={{
              p: 2.5,
              borderRadius: "16px",
              backgroundColor: "rgba(255, 255, 255, 0.12)",
              backdropFilter: "blur(8px)",
              color: "white",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              textAlign: "center",
            }}
          >
            <Typography variant="h3" fontWeight="800">
              {loadingStats ? "..." : stats.totalRequests}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, fontWeight: 600, mt: 0.5 }}>
              Total Requests Created
            </Typography>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 4 }}>
          <Card
            sx={{
              p: 2.5,
              borderRadius: "16px",
              backgroundColor: "rgba(255, 255, 255, 0.12)",
              backdropFilter: "blur(8px)",
              color: "white",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              textAlign: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
              <FireIcon sx={{ fontSize: 36, color: "#EF4444" }} />
              <Typography variant="h3" fontWeight="800">
                {stats.currentStreak}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.85, fontWeight: 600, mt: 0.5 }}>
              Active Daily Streak
            </Typography>
            <Chip
              label={getStreakMessage(stats.currentStreak)}
              size="small"
              sx={{
                mt: 1,
                backgroundColor: "rgba(255, 255, 255, 0.25)",
                color: "white",
                fontWeight: 700,
                borderRadius: "10px",
              }}
            />
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 4 }}>
          <Card
            sx={{
              p: 2.5,
              borderRadius: "16px",
              backgroundColor: "rgba(255, 255, 255, 0.12)",
              backdropFilter: "blur(8px)",
              color: "white",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              textAlign: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
              <StarIcon sx={{ fontSize: 36, color: "#F59E0B" }} />
              <Typography variant="h3" fontWeight="800">
                {stats.points || 0}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.85, fontWeight: 600, mt: 0.5 }}>
              Leaderboard Award Points
            </Typography>
            <Chip
              label={`${stats.points || 0} Points Awarded`}
              size="small"
              sx={{
                mt: 1,
                backgroundColor: "#FEF3C7",
                color: "#B45309",
                fontWeight: 700,
                borderRadius: "10px",
              }}
            />
          </Card>
        </Grid>
      </Grid>

      {stats.currentStreak > 0 && (
        <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid rgba(255, 255, 255, 0.15)" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 600 }}>
              7-Day Streak Milestone Progress
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {stats.currentStreak} / 7 Days
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min((stats.currentStreak / 7) * 100, 100)}
            sx={{
              height: 8,
              borderRadius: "4px",
              backgroundColor: "rgba(255,255,255,0.2)",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "#F59E0B",
                borderRadius: "4px",
              },
            }}
          />
        </Box>
      )}
    </Paper>
  );
};
